import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { VideoItem } from "@/mocks/videos";

export default function HeroCarousel({ slides }: { slides: VideoItem[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const goTo = (i: number) => setIndex((i + slides.length) % slides.length);
  const active = slides[index];

  return (
    <section className="relative w-full h-[480px] md:h-[620px] overflow-hidden bg-primary-950">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.title} 焦点推荐大图`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 flex items-center h-full w-full px-4 md:px-8 lg:px-12">
        <div key={active.id} className="max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-white text-xs font-medium mb-4">
            <i className="ri-fire-fill"></i>
            编辑精选推荐
          </span>
          <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-white leading-tight">{active.title}</h2>
          <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-white/85">
            <span className="px-2 py-0.5 rounded-md bg-white/15">{active.categoryLabel}</span>
            <span>{active.year}</span>
            <span className="w-1 h-1 rounded-full bg-white/50"></span>
            <span>{active.region}</span>
            <span className="w-1 h-1 rounded-full bg-white/50"></span>
            <span>{active.duration}</span>
            <span className="flex items-center gap-1 text-accent-400 font-semibold">
              <i className="ri-star-fill"></i>
              {active.rating}
            </span>
          </div>
          <p className="mt-4 text-sm md:text-base text-white/80 leading-relaxed line-clamp-3 max-w-xl">{active.description}</p>

          <div className="flex flex-wrap items-center gap-3 mt-7">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </Link>
            <Link
              to={`/category/${active.category}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white/15 backdrop-blur text-white text-sm font-semibold hover:bg-white/25 transition-colors whitespace-nowrap"
            >
              <i className="ri-grid-line"></i>
              更多{active.categoryLabel}
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        aria-label="上一张"
        className="absolute z-10 left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/30 transition-colors"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        aria-label="下一张"
        className="absolute z-10 right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/30 transition-colors"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute z-10 bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-accent-500" : "w-3 bg-white/50 hover:bg-white/80"}`}
          ></button>
        ))}
      </div>
    </section>
  );
}